'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { useTable } from 'src/context/table';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import { VOLUME_SELECT_OPTIONS } from 'src/components/constants';
import { PulseButton, PulseIconButton, Switch } from 'src/components/PulseUI';

interface Column {
  field: string;
  headerName: string;
}

interface GridColumnMenuProps {
  columns?: Column[];
  columnVisibilityModel: Record<string, boolean>;
  setColumnVisibilityModel: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  table?: string;
}

const GridColumnMenu = ({
  columns = [],
  columnVisibilityModel,
  setColumnVisibilityModel,
  table = '',
}: GridColumnMenuProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMounted, setIsMounted] = useState(false);
  const { showIcon } = useTable();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    setIsMobile(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setIsOpen(false);
  }, [isMobile]);

  const handleChange = (field: string) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const updateMenuPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX,
      });
    }
  };

  const handleClick = () => {
    if (!isOpen) {
      updateMenuPosition();
    }
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleMenuItemClick = (field: string) => {
    handleChange(field);
  };

  useClickOutside([menuRef, triggerRef], handleClose, isOpen);

  // Update position on scroll/resize when menu is open
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
  }, [isOpen]);

  const visibleColumns = useMemo(() => {
    if (!isOpen) return [];
    const newColumns = columns
      .filter(Boolean)
      .filter(
        (column) =>
          !VOLUME_SELECT_OPTIONS.map((option) => option.value).includes(
            column.field?.split('_')[1]
          )
      );

    return newColumns.sort((a, b) => {
      if (columnVisibilityModel[a.field] && !columnVisibilityModel[b.field]) {
        return -1;
      }
      if (!columnVisibilityModel[a.field] && columnVisibilityModel[b.field]) {
        return 1;
      }
      return 0;
    });
  }, [isOpen, columns, columnVisibilityModel]);

  const dropdownMenu = isOpen && (
    <div
      ref={menuRef}
      id={`${table}-column-menu`}
      data-testid={`${table}-column-menu`}
      className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-neutral-200 overflow-auto max-h-[300px]"
      style={{
        top: menuPosition.top,
        left: menuPosition.left,
        transform: 'translateX(-100%)',
      }}
      role="menu"
      aria-label="Column visibility options"
    >
      <ul className="list-none p-0 m-0">
        {visibleColumns?.map((column) => (
          <li
            id={`${table}-column-menu-item-${column.field}`}
            data-testid={`${table}-column-menu-item-${column.field}`}
            key={`column_${column.field}`}
            className="flex items-center gap-2 text-base font-medium py-2 px-4 cursor-pointer hover:bg-neutral-100"
            onClick={() => handleMenuItemClick(column.field)}
            role="menuitemcheckbox"
            aria-checked={!!columnVisibilityModel[column.field]}
          >
            <Switch
              size="sm"
              checked={!!columnVisibilityModel[column.field]}
              aria-label={`Toggle ${column.headerName} column visibility`}
              data-testid={`${table}-column-menu-item-switch-${column.field}`}
            />
            {column.headerName}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <div
        ref={triggerRef}
        id={isMobile || showIcon ? `${table}-column-menu-icon-button` : `${table}-column-menu-menu-button`}
        data-testid={isMobile || showIcon ? `${table}-column-menu-icon-button` : `${table}-column-menu-menu-button`}
      >
        {isMobile || showIcon ? (
          <PulseIconButton
            variant="soft"
            color="neutral"
            icon={faBars}
            onClick={handleClick}
            aria-label="Toggle column visibility menu"
            aria-haspopup={true}
            aria-expanded={isOpen}
            className="font-black"
            tooltip="Toggle columns"
          />
        ) : (
          <PulseButton
            variant="soft"
            color="neutral"
            startDecorator={faBars}
            onClick={handleClick}
            aria-label="Toggle column visibility menu"
            aria-haspopup={true}
            aria-expanded={isOpen}
            className="font-black"
          >
            Columns
          </PulseButton>
        )}
      </div>
      {isMounted && createPortal(dropdownMenu, document.body)}
    </>
  );
};

export default GridColumnMenu;
